/*
 * INDRAAZ — sticky bar + main-area quantity/cart control for products with
 * no in-theme bundle picker.
 *
 * Formerly waited to see whether the third-party Rapi app's sticky bar
 * ([id^="rapi-sticky-atc-root-"]) rendered anything before revealing these
 * controls, since both could otherwise show at once. Rapi is gone now —
 * the bundle picker (idz-bundle.js) is theme code that renders synchronously
 * with the rest of the page, and main-product.liquid already decides
 * server-side (idz_bundle_active) whether to render this fallback's markup
 * at all. So there is nothing left to wait for or race against: these
 * controls just reveal immediately on load.
 *
 * The quantity stepper and Add to Cart/View Cart button (idz-atc-sync-control
 * snippet, one instance in the main product area, one in the sticky bar) are
 * wired to idz-cart.js's existing data-idz-cart-form contract, which is what
 * actually keeps quantity and cart state synced between both instances and
 * the real cart drawer. This file only handles: revealing the controls, the
 * sticky bar's scroll show/hide, and the +/- stepper buttons (idz-cart.js
 * listens for the input's "change" event but doesn't provide the +/-
 * buttons).
 */
(function () {
  if (window.__idzAtcSyncBound) return;
  window.__idzAtcSyncBound = true;

  var NEAR_TOP_PX = 40;

  function syncQtyAcross(sourceInput, value) {
    document.querySelectorAll('[data-idz-atc-sync] [data-idz-qty-input]').forEach(function (input) {
      if (input !== sourceInput) input.value = value;
    });
  }

  function bindStepper(form) {
    var input = form.querySelector('[data-idz-qty-input]');
    var minus = form.querySelector('[data-idz-qty-minus]');
    var plus = form.querySelector('[data-idz-qty-plus]');
    if (!input) return;

    function commit(newValue) {
      var min = parseInt(input.min, 10);
      if (isNaN(min)) min = 1;
      if (newValue < min) newValue = min;
      input.value = newValue;
      syncQtyAcross(input, newValue);
      // idz-cart.js listens for "change" on this input to push the update
      // to the cart when the line is already there.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (minus) {
      minus.addEventListener('click', function () {
        commit((parseInt(input.value, 10) || 1) - 1);
      });
    }
    if (plus) {
      plus.addEventListener('click', function () {
        commit((parseInt(input.value, 10) || 0) + 1);
      });
    }
    input.addEventListener('change', function () {
      var value = parseInt(input.value, 10);
      if (isNaN(value)) value = 1;
      syncQtyAcross(input, value);
    });
    // Selects the whole number on focus so typing or backspacing replaces it
    // immediately, instead of landing at the start and needing to be moved to
    // the end first. Matches the same fix applied to Dawn's own quantity
    // inputs (cart drawer, cart page) in global.js.
    input.addEventListener('focus', function () {
      input.select();
      // Forcing 16px font-size to stop iOS auto-zoom (see the CSS) also
      // removed the auto-scroll-into-view that used to come bundled with
      // that zoom, so it's restored explicitly here instead. Deferred so it
      // runs after the on-screen keyboard has actually opened.
      window.setTimeout(function () {
        input.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 300);
    });
  }

  // idz-cart.js dispatches this after every cart read/refresh with
  // { variantId: quantity }. Applied here since idz-cart.js itself only
  // touches the input's min, not its displayed value.
  var latestQuantities = null;
  function applyKnownQuantity(form) {
    if (!latestQuantities) return;
    var button = form.querySelector('[data-idz-cart-submit]');
    var input = form.querySelector('[data-idz-qty-input]');
    if (!button || !input) return;
    var vid = String(button.dataset.variantId);
    if (latestQuantities[vid] !== undefined) input.value = latestQuantities[vid];
  }

  document.addEventListener('idz:cart-quantities', function (event) {
    latestQuantities = event.detail || {};
    document.querySelectorAll('[data-idz-atc-sync]').forEach(applyKnownQuantity);
  });

  function revealSticky(bar) {
    bar.hidden = false;

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var mainForm = document.querySelector('[data-idz-atc-main-sync]');
        var rect = mainForm ? mainForm.getBoundingClientRect() : null;
        var pastBuyBox = rect ? rect.bottom < 0 : true;
        var atTop = window.scrollY <= NEAR_TOP_PX;
        bar.classList.toggle('idz-sticky-fallback--visible', pastBuyBox && !atTop);
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // Keeps --idz-kb-offset (consumed by .idz-sticky-fallback in idz-theme.css)
  // in sync with how much the on-screen keyboard is currently covering, so
  // the fixed-position sticky bar can shift up above it instead of staying
  // pinned to the bottom of the layout viewport, hidden behind the keyboard.
  function bindKeyboardOffset() {
    if (!window.visualViewport) return;
    var vv = window.visualViewport;
    function update() {
      var offset = window.innerHeight - vv.height - vv.offsetTop;
      document.documentElement.style.setProperty('--idz-kb-offset', Math.max(0, offset) + 'px');
    }
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
  }

  function revealMain(wrap) {
    wrap.hidden = false;
    // The plain Dawn "Add to cart" button rendered by the buy_buttons block
    // is this control's previous sibling (see main-product.liquid) — hide it
    // now that our quantity + Add to Cart/View Cart control is taking over.
    var prev = wrap.previousElementSibling;
    if (prev) prev.style.display = 'none';
  }

  function start() {
    var stickyBars = document.querySelectorAll('[data-idz-sticky-fallback]');
    var mainWraps = document.querySelectorAll('[data-idz-atc-main-sync]');
    var allForms = document.querySelectorAll('[data-idz-atc-sync]');
    if (!stickyBars.length && !mainWraps.length) return;

    allForms.forEach(bindStepper);
    if (stickyBars.length) bindKeyboardOffset();

    stickyBars.forEach(revealSticky);
    mainWraps.forEach(revealMain);
    allForms.forEach(applyKnownQuantity);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
